import React from 'react';
import { Check, ArrowRight, Users, Clock, Video, Monitor, Wifi, Zap } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function BetterOnlineMeetings() {
  const {
    t
  } = useLanguage();
  const benefits = t('betterOnlineMeetings.benefits') as string[];
  const sessions = t('betterOnlineMeetings.sessions') as Array<{
    title: string;
    desc: string;
    topicsLabel: string;
    topics: string[];
  }>;
  const forWhomItems = t('betterOnlineMeetings.forWhomItems') as string[];
  const forWhomIcons = [<Monitor className="text-blue-600 shrink-0" size={20} data-id="element-475" />, <Zap className="text-blue-600 shrink-0" size={20} data-id="element-476" />, <Users className="text-blue-600 shrink-0" size={20} data-id="element-477" />];
  return <PageTransition data-id="element-478">
      <div className="pt-12 pb-24" data-id="element-479">
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-480">
          <DecorativeShape type="circle" color="bg-blue-100" className="top-0 right-0 w-[400px] h-[400px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-481" />
          <DecorativeShape type="blob" color="bg-indigo-100" className="bottom-0 left-0 w-64 h-64 -translate-x-1/4 translate-y-1/4 -z-10" data-id="element-482" />
          <Animate className="text-center max-w-4xl mx-auto" data-id="element-483">
            <span className="inline-block px-4 py-2 bg-blue-50 text-blue-600 font-medium rounded-full text-sm mb-6" data-id="element-484">
              {t('betterOnlineMeetings.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-485">
              {t('betterOnlineMeetings.title')} <br data-id="element-486" />
              <span className="text-blue-600" data-id="element-487">
                {t('betterOnlineMeetings.titleHighlight')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-488">
              {t('betterOnlineMeetings.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm font-medium text-gray-500 mb-10" data-id="element-489">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-490">
                <Clock size={18} className="text-blue-600" data-id="element-491" />
                <span data-id="element-492">{t('betterOnlineMeetings.duration')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-493">
                <Video size={18} className="text-blue-600" data-id="element-494" />
                <span data-id="element-495">{t('betterOnlineMeetings.audience')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-496">
                <Wifi size={18} className="text-blue-600" data-id="element-497" />
                <span data-id="element-498">{t('betterOnlineMeetings.online')}</span>
              </div>
            </div>
            <Link to="/contact" data-id="element-499">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-blue-200" size="lg" rightIcon={<ArrowRight size={20} data-id="element-501" />} data-id="element-500">
                {t('betterOnlineMeetings.bookCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-502">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-503">
            <div className="lg:col-span-2 space-y-12" data-id="element-504">
              <Animate data-id="element-505">
                <h2 className="text-3xl font-bold mb-6 text-charcoal" data-id="element-506">
                  {t('betterOnlineMeetings.aboutTitle')}
                </h2>
                <div className="prose prose-lg text-gray-600" data-id="element-507">
                  <p className="mb-4" data-id="element-508">{t('betterOnlineMeetings.aboutP1')}</p>
                  <p data-id="element-509">{t('betterOnlineMeetings.aboutP2')}</p>
                </div>
              </Animate>
              <Animate delay={0.1} data-id="element-510">
                <h3 className="text-2xl font-bold mb-6 text-charcoal" data-id="element-511">
                  {t('betterOnlineMeetings.learnTitle')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-512">
                  {benefits.map((benefit: string, index: number) => <div key={index} className="flex items-start gap-3" data-id="element-513">
                      <div className="mt-1 p-1 rounded-full bg-blue-50 text-blue-600 flex-shrink-0" data-id="element-514">
                        <Check size={14} strokeWidth={3} data-id="element-515" />
                      </div>
                      <span className="text-gray-700" data-id="element-516">{benefit}</span>
                    </div>)}
                </div>
              </Animate>
              <Animate delay={0.15} data-id="element-517">
                <h3 className="text-2xl font-bold mb-6 text-charcoal" data-id="element-518">
                  {t('betterOnlineMeetings.structureTitle')}
                </h3>
                <div className="space-y-6" data-id="element-519">
                  {sessions.map((session, index) => <Card key={index} className="p-6 border-l-4 border-blue-600 bg-white" data-id="element-520">
                      <h4 className="text-xl font-bold text-charcoal mb-2" data-id="element-521">
                        {session.title}
                      </h4>
                      <p className="text-gray-600 mb-4" data-id="element-522">{session.desc}</p>
                      <div className="bg-blue-50 p-4 rounded-xl" data-id="element-523">
                        <p className="text-sm font-bold text-blue-800 mb-2" data-id="element-524">
                          {session.topicsLabel}
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-525">
                          {session.topics.map((topic: string, i: number) => <li key={i} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-526">
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-600" data-id="element-527" />
                              {topic}
                            </li>)}
                        </ul>
                      </div>
                    </Card>)}
                </div>
              </Animate>
            </div>
            <div className="lg:col-span-1" data-id="element-528">
              <div className="sticky top-24 space-y-8" data-id="element-529">
                <Card className="p-8 bg-white shadow-xl border-t-4 border-blue-600" data-id="element-530">
                  <h3 className="text-xl font-bold mb-6 text-charcoal" data-id="element-531">
                    {t('betterOnlineMeetings.forWhom')}
                  </h3>
                  <ul className="space-y-4 mb-8" data-id="element-532">
                    {forWhomItems.map((item: string, i: number) => <li key={i} className="flex items-start gap-3" data-id="element-533">
                        {forWhomIcons[i]}
                        <span className="text-gray-600" data-id="element-534">{item}</span>
                      </li>)}
                  </ul>
                  <Link to="/contact" className="block" data-id="element-535">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-blue-200" rightIcon={<ArrowRight size={18} data-id="element-537" />} data-id="element-536">
                      {t('betterOnlineMeetings.inquiry')}
                    </Button>
                  </Link>
                </Card>
                <div className="bg-blue-50 rounded-3xl p-8 text-center" data-id="element-538">
                  <div className="w-16 h-16 mx-auto bg-white rounded-full flex items-center justify-center shadow-md mb-4 text-blue-600" data-id="element-539">
                    <Video size={32} data-id="element-540" />
                  </div>
                  <h4 className="font-bold text-lg mb-2" data-id="element-541">
                    {t('betterOnlineMeetings.privateTitle')}
                  </h4>
                  <p className="text-gray-600 text-sm mb-4" data-id="element-542">
                    {t('betterOnlineMeetings.privateDesc')}
                  </p>
                  <Link to="/contact" className="text-blue-600 font-bold hover:underline" data-id="element-543">
                    {t('betterOnlineMeetings.contactLink')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-544">
          <Animate data-id="element-545">
            <Card className="p-12 bg-gradient-to-br from-blue-50 to-indigo-50 border-0" data-id="element-546">
              <h2 className="text-3xl font-bold mb-4" data-id="element-547">
                {t('betterOnlineMeetings.ctaTitle')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-548">
                {t('betterOnlineMeetings.ctaDesc')}
              </p>
              <Link to="/contact" data-id="element-549">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-blue-200" rightIcon={<ArrowRight size={18} data-id="element-551" />} data-id="element-550">
                  {t('betterOnlineMeetings.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}